import type { TeamCreationJobListResponse } from "@bezoned/shared/types/api/team/team-creation-job-list.types.js";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { createTeam } from "../../../services/api.service.js";
import { teamCreationJobsQueryKey } from "../team-list/team-creation-jobs.query.js";
import { CreateTeamSubmitError } from "./create-team-submit-error.js";

export const useCreateTeamDialog = (props: {
  open: boolean;
  onCreateTeamError?: (error: CreateTeamSubmitError) => void;
  setOpen: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const dialogOpenGenerationRef = useRef(0);

  useEffect(() => {
    if (!props.open) {
      return;
    }

    dialogOpenGenerationRef.current += 1;
    setLoading(false);
  }, [props.open]);

  const handleCreateTeam = useCallback(
    async (data: Parameters<typeof createTeam>[0]) => {
      const requestDialogGeneration = dialogOpenGenerationRef.current;
      const isCurrentDialogRequest = () =>
        requestDialogGeneration === dialogOpenGenerationRef.current;

      try {
        setLoading(true);
        const { jobId } = await createTeam(data);
        const createdAt = new Date().toISOString();

        if (isCurrentDialogRequest()) {
          setLoading(false);
          props.setOpen(false);
        }

        queryClient.setQueryData<TeamCreationJobListResponse>(
          teamCreationJobsQueryKey,
          (currentJobs = []) => {
            if (currentJobs.some((job) => job.jobId === jobId)) {
              return currentJobs;
            }

            return [
              {
                acknowledgedAt: null,
                createdAt,
                failureMessage: null,
                jobId,
                msTeamId: null,
                requestedName:
                  ("name" in data ? data.name : undefined) ??
                  data.msTeamId ??
                  "Unnamed office",
                status: "pending",
                updatedAt: createdAt,
              },
              ...currentJobs,
            ];
          }
        );
        queryClient
          .invalidateQueries({
            queryKey: teamCreationJobsQueryKey,
          })
          .catch(() => {
            // The optimistic pending row keeps polling active until the next successful fetch.
          });
        queryClient.invalidateQueries({ queryKey: ["teamsGet"] }).catch(() => {
          // The job row is already visible; metadata will refresh on the next successful query.
        });

        return {
          ok: true as const,
        };
      } catch (error) {
        if (!isCurrentDialogRequest()) {
          return {
            ok: true as const,
          };
        }

        const submitError =
          error instanceof CreateTeamSubmitError
            ? error
            : new CreateTeamSubmitError({
                message: "Failed to create office. Please try again.",
              });

        props.onCreateTeamError?.(submitError);

        if (submitError.code === "starter-office-limit-reached") {
          props.setOpen(false);
          queryClient
            .invalidateQueries({ queryKey: ["teamsGet"] })
            .catch(() => {
              // Refetch metadata when another user consumed the Starter slot.
            });
        }

        return {
          error: submitError,
          ok: false as const,
        };
      } finally {
        if (isCurrentDialogRequest()) {
          setLoading(false);
        }
      }
    },
    [props.onCreateTeamError, props.setOpen, queryClient]
  );

  return {
    loading,
    open: props.open,

    props: {
      handleCreateTeam,
      loading,
      open: props.open,
      setOpen: props.setOpen,
    },
    setOpen: props.setOpen,
  };
};
